import { getApiUrl, getAuthHeaders } from './api-client';

/**
 * Один и тот же набор админских запросов обслуживает и взрослый Placement
 * Test, и детский тест: у них одинаковые эндпоинты, отличается только
 * префикс пути. Поэтому запросы собираются фабрикой, а разделы админки
 * получают готовые функции для своего теста.
 *
 * @param {string} basePath — '/admin/placement-tests' или '/admin/kids-tests'
 */
export function createTestAdminApi(basePath) {
    function buildUrl(apiUrl, path, params = {}) {
        const queryParams = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        }

        const queryString = queryParams.toString();

        return `${apiUrl}${basePath}${path}${queryString ? `?${queryString}` : ''}`;
    }

    /**
     * GET {basePath}/templates — список всех шаблонов теста
     */
    async function getTemplates(params = {}) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return [];
            }

            const response = await fetch(buildUrl(apiUrl, '/templates', params), {
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                }
            });

            if (!response.ok) {
                console.error(`Failed to fetch templates. Status: ${response.status}`);
                return [];
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching test templates:", error);
            return [];
        }
    }

    /**
     * GET {basePath}/templates/{templateId} — шаблон по ID
     */
    async function getTemplateById(templateId) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                throw new Error('API URL is not configured');
            }

            const response = await fetch(buildUrl(apiUrl, `/templates/${templateId}`), {
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                }
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMsg = data?.message || `Failed to fetch template. Status: ${response.status}`;
                console.error('Error fetching template:', errorMsg, data);
                throw new Error(errorMsg);
            }

            return data;
        } catch (error) {
            console.error("Error fetching test template by id:", error);
            throw error;
        }
    }

    /**
     * POST {basePath}/templates — создать новый шаблон теста
     */
    async function createTemplate(templateData) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return { error: true, message: 'API URL is not configured' };
            }

            const response = await fetch(buildUrl(apiUrl, '/templates'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(templateData),
            });

            const data = await response.json();

            if (!response.ok) {
                console.error(`Failed to create template. Status: ${response.status}`, data);
                return { error: true, ...data };
            }

            return data;
        } catch (error) {
            console.error("Error creating test template:", error);
            return { error: true, message: error.message };
        }
    }

    /**
     * PUT {basePath}/templates/{templateId} — обновить шаблон теста
     */
    async function updateTemplate(templateId, templateData) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return { error: true, message: 'API URL is not configured' };
            }

            const response = await fetch(buildUrl(apiUrl, `/templates/${templateId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(templateData),
            });

            const data = await response.json();

            if (!response.ok) {
                console.error(`Failed to update template. Status: ${response.status}`, data);
                return { error: true, ...data };
            }

            return data;
        } catch (error) {
            console.error("Error updating test template:", error);
            return { error: true, message: error.message };
        }
    }

    /**
     * DELETE {basePath}/templates/{templateId} — удалить шаблон теста
     */
    async function deleteTemplate(templateId) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return { error: true, message: 'API URL is not configured' };
            }

            const response = await fetch(buildUrl(apiUrl, `/templates/${templateId}`), {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                },
            });

            if (response.status === 204) {
                return { ok: true };
            }

            const data = await response.json();

            if (!response.ok) {
                console.error(`Failed to delete template. Status: ${response.status}`, data);
                return { error: true, ...data };
            }

            return data;
        } catch (error) {
            console.error("Error deleting test template:", error);
            return { error: true, message: error.message };
        }
    }

    /**
     * GET {basePath}/sessions — список сессий прохождения
     */
    async function getSessions(params = {}) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return { items: [], total: 0, page: 1, limit: 50, totalPages: 0 };
            }

            const response = await fetch(buildUrl(apiUrl, '/sessions', params), {
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                }
            });

            if (!response.ok) {
                console.error(`Failed to fetch sessions. Status: ${response.status}`);
                return { items: [], total: 0, page: 1, limit: 50, totalPages: 0 };
            }

            const data = await response.json();
            return {
                items: data.items || [],
                total: data.total || 0,
                page: data.page || 1,
                limit: data.limit || 50,
                totalPages: data.totalPages || Math.ceil((data.total || 0) / (data.limit || 50)),
                meta: data
            };
        } catch (error) {
            console.error("Error fetching test sessions:", error);
            return { items: [], total: 0, page: 1, limit: 50, totalPages: 0 };
        }
    }

    /**
     * GET {basePath}/sessions/{sessionId} — сессия по ID вместе с разбором ответов
     */
    async function getSessionById(sessionId) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                throw new Error('API URL is not configured');
            }

            const response = await fetch(buildUrl(apiUrl, `/sessions/${sessionId}`), {
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                }
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMsg = data?.message || `Failed to fetch session. Status: ${response.status}`;
                console.error('Error fetching session:', errorMsg, data);
                throw new Error(errorMsg);
            }

            return data;
        } catch (error) {
            console.error("Error fetching test session by id:", error);
            throw error;
        }
    }

    /**
     * DELETE {basePath}/sessions/{sessionId} — удалить сессию
     */
    async function deleteSession(sessionId) {
        try {
            const apiUrl = getApiUrl();
            if (!apiUrl) {
                return { error: true, message: 'API URL is not configured' };
            }

            const response = await fetch(buildUrl(apiUrl, `/sessions/${sessionId}`), {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders(),
                },
            });

            if (response.status === 204) {
                return { ok: true };
            }

            const data = await response.json();

            if (!response.ok) {
                console.error(`Failed to delete session. Status: ${response.status}`, data);
                return { error: true, ...data };
            }

            return data;
        } catch (error) {
            console.error("Error deleting test session:", error);
            return { error: true, message: error.message };
        }
    }

    return {
        getTemplates,
        getTemplateById,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        getSessions,
        getSessionById,
        deleteSession,
    };
}

const placementTestAdminApi = createTestAdminApi('/admin/placement-tests');

export const getAdminPlacementTestTemplates = placementTestAdminApi.getTemplates;
export const getAdminPlacementTestTemplateById = placementTestAdminApi.getTemplateById;
export const createAdminPlacementTestTemplate = placementTestAdminApi.createTemplate;
export const updateAdminPlacementTestTemplate = placementTestAdminApi.updateTemplate;
export const deleteAdminPlacementTestTemplate = placementTestAdminApi.deleteTemplate;
export const getAdminPlacementTestSessions = placementTestAdminApi.getSessions;
export const getAdminPlacementTestSessionById = placementTestAdminApi.getSessionById;
export const deleteAdminPlacementTestSession = placementTestAdminApi.deleteSession;
